














































































import {Component, Prop, Vue, Watch, Ref} from 'vue-property-decorator'
import {VideoShot} from '@/store/shots'
import {getUUID} from '@/store/store-utils'
import {upload} from '@/asset'
import Params from '@/components/Params.vue'
import {projectStore, shotStore} from "@/store"

/**
 * Notes on time usage:
 * The project store keeps the local (and global) time.
 * The video element also has its idea of current time.
 * We want to keep these in sync, in the following ways:
 * When the video isn't playing:
 * - Clicking/dragging the CTI should update the video's current time
 * - Moving the video's time in playbar should update local time and
 *   CTI (so they stay in sync)
 * When the video is playing:
 * - local time (and CTI) should advance as video plays
 * - clicking/dragging CTI should seek video to new time (continue playing)
 *
 * To do this, the components communicate as follows:
 * - KeyframeGraph (has CTI):
     - takes currentTime as prop
     - emits update-time when user moves CTI
 * - Timeline (contains KeyframeGraph) passes through update-time
 * - VideoShotEditor emits play, stop, and:
     - emits update-time as video plays, and when user clicks playbar
     - Watches project.localTime; updates video time when it changes
       - but not when playing -- that would cause a loop
 * - ShotDesigner (contains Timeline and VideoShotEditor):
     - receives Timeline update-time
       - sets video time if video is playing, else sets project
         localTime. (Sets video time using videoTime setter in VideoShotEditor)
     - Also receives VideoShotEditor update-time
       - sets project.localTime -- this is OK even when video is
         playing because it won't try to seek when localTime is
         updated while it's playing
 *
 */


@Component({
  components: {
    Params
  }
})
export default class VideoShotEditor extends Vue {
  @Prop() shot!: VideoShot

  @Ref() readonly video!: HTMLVideoElement
  @Ref() readonly files!: HTMLElement | HTMLElement[][]
  @Ref() readonly currentFrameShot!: any

  loading = false
  isPlaying = false

  shotVideoUrl = ''
  videoLoading = true
  // videoSeeking = false
  private reqAnimFrameCbUid!: number | null

  mounted() {
    this.setupShotVideoUrl()
  }

  updated() {
    this.setupShotVideoUrl()
  }

  beforeDestroy() {
    this.teardownReqAnimFrameCb()
  }

  private setupReqAnimFrameCb() {
    const rafCb = (ts: number) => {
      if (this.video && this.video.currentTime >= this.shot.outPoint)
        this.video.pause()
      window.requestAnimationFrame(rafCb)
    }
    this.reqAnimFrameCbUid = window.requestAnimationFrame(rafCb)
  }

  private teardownReqAnimFrameCb() {
    if (this.reqAnimFrameCbUid)
      window.cancelAnimationFrame(this.reqAnimFrameCbUid)
    this.reqAnimFrameCbUid = null
  }

  @Watch('shot.videoFile')
  onVideoFileChanged() {
    this.videoLoading = true
  }

  get currentTime() {
    return projectStore.localTime
  }

  get videoSrcUrl() {
    return this.shot.videoFile
  }

  setVideoTime(time: number) { // in sec
    this.video.currentTime = time
  }

  setPlaying(shouldPlay: boolean) {
    if (shouldPlay) {
      if (!this.isPlaying)
        this.video.play()
    } else {
      if (this.isPlaying)
        this.video.pause()
    }
  }

  async onPlay(e: Event) {
    this.isPlaying = true
    this.$emit('play')
    this.setupReqAnimFrameCb()
  }

  async onStop(e: Event) {
    this.isPlaying = false
    this.$emit('stop')
    this.teardownReqAnimFrameCb()
  }

  onVideoLoaded(e: Event) {
    projectStore.setLocalTime(this.shot.inPoint)
    this.videoLoading = false
  }

  // TODO:  consider implementing spinner showed while seeking video
  /*
  onVideoSeeking(e: Event) {
    console.log('onVideoSeeking')
    if (!this.videoSeeking)
      this.videoSeeking = true
  }

  onVideoSeeked(e: Event) {
    console.log('onVideoSeeked')
    if (this.videoSeeking)
      this.videoSeeking = false
  }
  */

  async onDurationChanged(e: Event) {
    this.$emit('update-shot-duration', this.video.duration)
  }

  async onTimeUpdated(e: Event) {
    // TODO: consider debouncing this (e.g. using 'ts-debounce') It
    // emits several events immediately when the video.currentTime is
    // reset. But make sure it's still smooth when playing.
    if (this.video)
      this.$emit('update-time', this.video.currentTime)
  }

  // Set video time to current time (when user moves CTI etc.)
  @Watch('currentTime')
  onLocalTimeChanged() {
    // Don't update video time while playing; that'd be a loop.
    if (!this.isPlaying)
      this.video.currentTime = this.currentTime
  }

  pickFile() {
    let elt = this.$refs.files
    if (elt instanceof Array)
      elt = elt[0]            // choose first one if they picked more than one
    if (elt instanceof HTMLElement)
      elt.click()
    else
      this.$log.error('Picked invalid object type', elt)
  }

  async onFilePicked(e: InputEvent) {
    if (!e.target)
      return
    const files = (e.target as HTMLInputElement).files
    if (!files)
      return
    for (const file of files) {
      if (file !== undefined && file.name.lastIndexOf('.') > 0) {
        this.loading = true
        this.$log.debug(`Uploading`, file)
        const assetId = getUUID('asset')
        const progressCb = (progress: number) => {
          this.$log.info(`Upload of ${file.name} is ${progress}% done`)
        }
        try {
          const {downloadURL, storagePath} = await upload(assetId, 'video', file, 'mp4',
                                                          progressCb)
          this.$toast(`Video ${file.name} uploaded!`)
          this.$emit('update-shot-video', downloadURL)
        } catch (err) {
          this.$toast.error(`Error uploading ${file.name}: ${err}`)
        } finally {
          this.loading = false
        }
      }
    }
  }

  saveVideoUrl(videoUrl: string) {
    this.$log.info(`Update video URL: ${videoUrl}`)
    if (this.shot.videoFile !== videoUrl)
      shotStore.updateShotVideoUrl({shot: this.shot, videoUrl})
  }

  private setupShotVideoUrl() {
    const fbStorageRe = /^https:\/\/firebasestorage\.googleapis\.com\//
    if (this.shot.videoFile && this.shot.videoFile !== '') {
      if (fbStorageRe.test(this.shot.videoFile))
        this.shotVideoUrl = ''
      else
        this.shotVideoUrl = this.shot.videoFile
    }
  }

  // In/Out input handlers
  async updateInPoint(inPoint: string) {
    console.log(`in: ${inPoint}`)
    shotStore.setShotInPoint({shot: this.shot, inPoint: parseFloat(inPoint)})
  }
  async updateOutPoint(outPoint: string) {
    console.log(`out: ${outPoint}`)
    shotStore.setShotOutPoint({shot: this.shot, outPoint: parseFloat(outPoint)})
  }
}

